import { ReactElement, useEffect, useState } from 'react'
import { I18n, Translate, Translate2 } from 'common'
import { useNavigate } from 'react-router-dom'
import { breakpoints } from '../../../../theme/breakpoints'
import CloseIcon from '@mui/icons-material/Close'
// import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Select, Button, TextInput } from '../../../../ui-components'
import ListServiceProvider from '../../../../services/ListServiceProvider'
import {
  AddPartToList,
  ListResponseError,
} from '../../../../store/lists/interfaces'
import { StoreInstances } from '../../../../store/StoreInstancesContainer'
import { AxiosError } from 'axios'
import { ProductModel } from 'interfaces'
import { IdValueGeneric } from 'interfaces'
import styles from '../AddToListDrawer/AddToListDrawer.module.scss'

interface AddNewListProps {
  categories
  selectedProduct: ProductModel
  totalQty: number
  handleBack
  handleClose
}

type IselectedCatListGroup = IdValueGeneric<number, string>

export const AddNewList = (props: AddNewListProps): ReactElement => {
  const { categories, selectedProduct, totalQty, handleBack, handleClose } =
    props

  const navigate = useNavigate()
  const [disableCreateList, setDisableCreateList] = useState(true)
  const [newListCategoryId, setNewListCategoryId] = useState()
  const [newListName, setNewListName] = useState()
  const [newGroupName, setNewGroupName] = useState()
  const [selectedCategoryOption, setSelectedCategoryOption] =
    useState<IselectedCatListGroup>({
      id: null,
      value: null,
    })

  useEffect(() => {
    if (selectedCategoryOption && newListName) {
      setDisableCreateList(false)
    } else {
      setDisableCreateList(true)
    }
  }, [selectedCategoryOption, newListName])

  const handleSelectedCategoryNewList = (selectedCategory) => {
    setNewListCategoryId(selectedCategory.id)
    setSelectedCategoryOption(selectedCategory)
  }

  const handleAddNewListName = (value) => {
    setNewListName(value)
  }

  const handleAddNewGroupName = (value) => {
    setNewGroupName(value)
  }

  const handleListLocation = () => {
    return navigate('lists')
  }

  const handleCreateNewList = async (): Promise<void> => {
    const listData = {
      name: newListName,
      note: '',
      categoryId: newListCategoryId,
    }
    const partData = [
      {
        lineCode: selectedProduct.lineCode,
        partNumber: selectedProduct.partNumber,
        stockQty: totalQty,
        comment: '',
        partDescription: selectedProduct.description,
        handleListLocation,
        handleClose,
      },
    ]
    const groupData = newGroupName
    await createListAndGroup(listData, groupData, partData)
    handleBack()
  }

  const cutText = (text: string): string => {
    const maxLength = 4
    let cutText = text.substr(0, maxLength)

    // Check if the cut text ends with a period
    if (cutText.endsWith('.')) {
      return cutText
    }

    cutText = cutText.trim()

    // Check if there are spaces within the cut text
    const lastSpaceIndex = cutText.lastIndexOf(' ')
    if (lastSpaceIndex !== -1) {
      cutText = cutText.substr(0, lastSpaceIndex)
    }

    return cutText + '...'
  }

  const createListAndGroup = async (
    requestData: {
      name: string
      note: string
      categoryId: number
    },
    groupData: string,
    req: Array<AddPartToList>
  ): Promise<void> => {
    try {
      const responseCreateList =
        await ListServiceProvider.createList(requestData)
      let groupDefaultId = responseCreateList?.groups[0]?.id

      if (responseCreateList.id !== null && responseCreateList.id > 0) {
        if (groupData) {
          const { id: groupId } = await ListServiceProvider.createGroup(
            responseCreateList.id,
            groupData
          )
          groupDefaultId = groupId
        }

        const status = await ListServiceProvider.addPartToGroup(
          responseCreateList.id,
          groupDefaultId,
          req
        )
        if (status === 200 || status === 206) {
          let description = `${req[0]?.partDescription} (${req[0]?.partNumber})`
          const isTablet = window.innerWidth < breakpoints.tablet
          if (isTablet) {
            description = cutText(description)
          }
          StoreInstances.uiStore.displaySuccessNotification(
            Translate2('itemAddedToList', [description]),
            {
              text: 'viewLists',
              action: req[0]?.handleListLocation,
            }
          )

          req[0]?.handleClose()
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 400) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }
      if ((e as AxiosError).response.status === 403) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        const responseMessage = message.split(':')
        StoreInstances.uiStore.displayErrorNotification(responseMessage[1])
      }
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification(
          'List name already exists'
        )
      }
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <p className={styles.styledText}>
          <ArrowBackIcon className={styles.back} onClick={handleBack} />
          <I18n name="addNewList" />
        </p>
        <CloseIcon className={styles.styledCloseIcon} onClick={handleClose} />
      </div>
      <div className={styles.selectsWrapper}>
        <p className={styles.selectsLabels}>
          {' '}
          <I18n name="category" />
        </p>
        <div className={styles.selectWrapper}>
          <Select
            optionFontSize="16px"
            options={categories ? categories : []}
            selectedOption={selectedCategoryOption}
            onSelect={handleSelectedCategoryNewList}
          />
        </div>
        <p className={styles.selectsLabels}>
          <I18n name="listName" />
        </p>
        <TextInput
          value={null}
          onChange={(value) => {
            handleAddNewListName(value)
          }}
          noMargin
          width="100%"
        />

        <p className={styles.selectsLabels}>
          {' '}
          <I18n name="groupName" />
        </p>
        <TextInput
          value={null}
          onChange={(value) => handleAddNewGroupName(value)}
          noMargin
          width="100%"
        />
      </div>

      <div className={styles.buttonsWrapper}>
        <Button
          onClick={handleClose}
          variant="secondary"
          size="medium"
          text={Translate('cancel')}
        />

        <Button
          onClick={handleCreateNewList}
          type="submit"
          variant="primary"
          size="medium"
          text={Translate('createList')}
          disabled={disableCreateList}
        />
      </div>
    </div>
  )
}
